/**
 * ChessScenario — interactive chess board that validates a target line of moves
 * against a chess.js engine. Renders side-by-side with a basketball-parallel
 * SVG animation. Reports completion back to the SAA backend.
 */
import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Chess } from 'chess.js';
import { Chessboard } from 'react-chessboard';
import { CheckCircle2, XCircle, RotateCcw, Play, Trophy, ArrowRight, Zap } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import BasketballParallel from './BasketballParallel';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });

const ChessScenario = ({ courseId, week, sideGame, previousAttempt }) => {
  const { module, kind, title, tactic, starting_fen: startFen, target_line: targetLine = [] } = sideGame;
  const escapeMode = kind === 'escape';

  // Build a fresh engine at the starting position on every mount / reset
  const [engine] = useState(() => new Chess(startFen));
  const [fen, setFen] = useState(engine.fen());
  const [moves, setMoves] = useState([]);            // SAN moves played so far
  const [status, setStatus] = useState('playing');   // playing | correct | wrong | complete
  const [attempts, setAttempts] = useState(previousAttempt?.attempts || 0);
  const [completed, setCompleted] = useState(!!previousAttempt?.completed);
  const [bonusMsg, setBonusMsg] = useState('');
  const startTimeRef = useRef(Date.now());

  const orientation = useMemo(() => {
    // "Escape" scenarios: student plays whichever side is to move at the FEN
    // For Set Trap Week 4 Noah's Ark and Trap Press, student plays Black (target line begins with Black's move)
    return engine.turn() === 'w' ? 'white' : 'black';
  }, [engine]);

  const reset = useCallback(() => {
    const e = new Chess(startFen);
    setFen(e.fen());
    setMoves([]);
    setStatus('playing');
    // Also reset the shared engine so future onPieceDrop uses fresh state
    engine.load(startFen);
    startTimeRef.current = Date.now();
  }, [startFen, engine]);

  const logResult = useCallback(async (didFinish, playedMoves) => {
    try {
      const r = await api.post(
        `/academy/electives/${courseId}/scenario`,
        {
          course_id: courseId,
          week,
          module,
          kind,
          completed: didFinish,
          moves_played: playedMoves,
          attempts: 1,
          total_time_seconds: Math.round((Date.now() - startTimeRef.current) / 1000),
        },
        { headers: authHeaders() },
      );
      if (r.data?.week_double_bonus) {
        toast.success(`🔥 +${r.data.bonus_amount} Scholar Dollars — Week ${week} Double Trap Bonus!`);
        setBonusMsg(`+${r.data.bonus_amount} Scholar Dollars · Week ${week} Double Trap Bonus`);
      }
    } catch (e) {
      // non-fatal
      console.warn('scenario log failed', e);
    }
  }, [courseId, week, module, kind]);

  const onPieceDrop = (from, to, piece) => {
    if (status === 'complete') return false;
    // Try to make the move (auto-promote to queen for simplicity)
    let move;
    try {
      move = engine.move({ from, to, promotion: 'q' });
    } catch {
      return false;
    }
    if (!move) return false;

    const nextMoves = [...moves, move.san];
    setMoves(nextMoves);
    setFen(engine.fen());

    // Check if the played move matches the next expected move in the target line
    const expected = targetLine[moves.length];
    if (!expected) {
      // No target left — treat any legal move as "explored"
      setStatus('playing');
      return true;
    }
    const norm = (s) => s.replace(/[+#!?]/g, '').replace(/\s/g, '');
    const isMatch = norm(move.san) === norm(expected);
    if (!isMatch) {
      setStatus('wrong');
      setAttempts((a) => a + 1);
      return true;
    }
    // If more moves remain
    if (nextMoves.length < targetLine.length) {
      setStatus('correct');
      // For 'set trap' scenarios where opponent has scripted responses,
      // we'd normally auto-play the opponent's move here — but our
      // target_line contains only the student's moves, so we simply wait.
      return true;
    }
    // Full target line complete — mark done
    setStatus('complete');
    setAttempts((a) => a + 1);
    setCompleted(true);
    logResult(true, nextMoves);
    return true;
  };

  const progress = Math.min(moves.length, targetLine.length);
  const totalTargetMoves = targetLine.length;
  const pct = totalTargetMoves > 0 ? Math.round((progress / totalTargetMoves) * 100) : 0;

  return (
    <div
      className="border-2 p-4 bg-black/40"
      style={{ borderColor: escapeMode ? '#3B82F6' : '#EF4444' }}
      data-testid={`chess-scenario-${module}`}
    >
      {/* Header */}
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <span
          className="text-[9px] font-black tracking-[0.28em] uppercase px-2 py-0.5"
          style={{ backgroundColor: escapeMode ? '#3B82F6' : '#EF4444', color: '#fff' }}
        >
          Module {module} · {escapeMode ? 'Escape' : 'Set'}
        </span>
        {completed && (
          <span className="inline-flex items-center gap-1 text-[9px] font-black tracking-widest uppercase text-emerald-400">
            <Trophy className="w-3 h-3" /> Cleared
          </span>
        )}
      </div>
      <div className="text-sm font-black text-white">{title}</div>
      <p className="text-xs text-zinc-400 mt-1.5 leading-relaxed">{tactic}</p>

      {/* Chess board + basketball parallel side-by-side */}
      <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left: Chess board */}
        <div>
          <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500 mb-2">The Board</div>
          <div style={{ maxWidth: 400 }}>
            <Chessboard
              position={fen}
              onPieceDrop={onPieceDrop}
              boardOrientation={orientation}
              customDarkSquareStyle={{ backgroundColor: '#8B6C42' }}
              customLightSquareStyle={{ backgroundColor: '#F5ECC8' }}
              areArrowsAllowed={true}
              customBoardStyle={{ borderRadius: '0', boxShadow: '0 0 0 1px #C9A96140' }}
            />
          </div>

          {/* Progress bar & status */}
          <div className="mt-3">
            <div className="flex justify-between text-[10px] font-black tracking-widest uppercase mb-1">
              <span className="text-zinc-500">Target Line</span>
              <span className={status === 'complete' ? 'text-emerald-400' : status === 'wrong' ? 'text-red-400' : 'text-zinc-400'}>
                {progress}/{totalTargetMoves} moves
              </span>
            </div>
            <div className="h-1.5 bg-zinc-900 overflow-hidden">
              <div
                className="h-full transition-all"
                style={{
                  width: `${pct}%`,
                  backgroundColor: status === 'complete' ? '#10B981' : status === 'wrong' ? '#EF4444' : '#C9A961',
                }}
              />
            </div>
            <div className="mt-1 text-[10px] font-mono text-zinc-500 truncate">
              Play: {targetLine.map((m, i) => (
                <span key={i} className={i < progress ? 'text-emerald-400' : 'text-zinc-600'}>
                  {i > 0 ? ' → ' : ''}{m}
                </span>
              ))}
            </div>
          </div>

          {/* Feedback + controls */}
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            {status === 'wrong' && (
              <div className="text-[11px] text-red-400 font-bold inline-flex items-center gap-1" data-testid={`chess-feedback-wrong-${module}`}>
                <XCircle className="w-3.5 h-3.5" /> Not the target move — try again. Reset the board and think about the trap geometry.
              </div>
            )}
            {status === 'correct' && (
              <div className="text-[11px] text-emerald-400 font-bold inline-flex items-center gap-1" data-testid={`chess-feedback-correct-${module}`}>
                <CheckCircle2 className="w-3.5 h-3.5" /> Correct! Keep going — {totalTargetMoves - progress} to complete.
              </div>
            )}
            {status === 'complete' && (
              <div className="text-[11px] text-emerald-400 font-black inline-flex items-center gap-1" data-testid={`chess-feedback-complete-${module}`}>
                <Trophy className="w-3.5 h-3.5" /> {escapeMode ? 'Trap escaped!' : 'Trap sprung!'} Full sequence executed.
              </div>
            )}
            <button
              onClick={reset}
              className="ml-auto inline-flex items-center gap-1 bg-zinc-800 hover:bg-zinc-700 text-white px-3 py-1.5 text-[10px] font-black uppercase tracking-widest"
              data-testid={`chess-reset-${module}`}
            >
              <RotateCcw className="w-3 h-3" /> Reset
            </button>
          </div>
          {bonusMsg && (
            <div className="mt-2 border border-amber-500/50 bg-amber-500/10 text-amber-300 px-2 py-1.5 text-[10px] font-black tracking-widest uppercase inline-flex items-center gap-1">
              <Zap className="w-3 h-3" /> {bonusMsg}
            </div>
          )}
        </div>

        {/* Right: Basketball parallel animation */}
        <div>
          <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500 mb-2">The Court Parallel</div>
          <BasketballParallel week={week} module={module} progress={progress} total={totalTargetMoves} kind={kind} />
        </div>
      </div>
    </div>
  );
};

export default ChessScenario;
